import React from 'react';
import type { SearchSupportInsight } from '../../types/messages';

type Props = {
  text: string;
  insight: SearchSupportInsight;
};

const SearchSupportInsightMessage = ({ text, insight }: Props) => {
  return (
    <div style={styles.card}>
      <div style={styles.lead}>{text}</div>
      <div style={styles.title}>{insight.title}</div>
      <div style={styles.summary}>{insight.summary}</div>
      {insight.metrics.length > 0 && (
        <div style={styles.metricGrid}>
          {insight.metrics.map(metric => (
            <div key={metric.label} style={styles.metricCard}>
              <div style={styles.metricLabel}>{metric.label}</div>
              <div style={styles.metricValue}>{metric.value}</div>
            </div>
          ))}
        </div>
      )}
      {insight.table && insight.table.rows.length > 0 && (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {insight.table.columns.map(column => (
                  <th key={column.key} style={styles.tableHead}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {insight.table.rows.map((row, index) => (
                <tr key={`${row.date || index}`}>
                  {insight.table?.columns.map(column => {
                    const isHighlighted = insight.table?.highlightedDates?.includes(row.date || '') === true;
                    const cellStyle = isHighlighted
                      ? { ...styles.tableCell, ...styles.tableCellHighlighted }
                      : styles.tableCell;

                    return (
                      <td key={column.key} style={cellStyle}>
                        {row[column.key] || ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {insight.missingRequirements && insight.missingRequirements.length > 0 && (
        <div style={styles.note}>{insight.missingRequirements.join(' ')}</div>
      )}
      {insight.nextAction && <div style={styles.nextAction}>{insight.nextAction}</div>}
    </div>
  );
};

const styles = {
  card: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid #d9e4ef',
    backgroundColor: '#f7fbff',
    boxShadow: '0 8px 24px rgba(20, 55, 90, 0.08)'
  },
  lead: {
    color: '#35526d',
    fontSize: '14px',
    lineHeight: 1.6
  },
  title: {
    color: '#11324d',
    fontSize: '18px',
    fontWeight: 700
  },
  summary: {
    color: '#35526d',
    fontSize: '14px',
    lineHeight: 1.7
  },
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '10px'
  },
  metricCard: {
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid #d8e4ee',
    padding: '12px'
  },
  metricLabel: {
    color: '#617a90',
    fontSize: '12px',
    marginBottom: '6px'
  },
  metricValue: {
    color: '#11324d',
    fontSize: '16px',
    fontWeight: 700
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    borderRadius: '12px',
    border: '1px solid #d8e4ee',
    backgroundColor: '#ffffff'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    minWidth: '360px'
  },
  tableHead: {
    textAlign: 'left' as const,
    padding: '10px 12px',
    fontSize: '12px',
    color: '#617a90',
    backgroundColor: '#eef5fb',
    borderBottom: '1px solid #d8e4ee'
  },
  tableCell: {
    padding: '10px 12px',
    fontSize: '13px',
    color: '#24435e',
    borderBottom: '1px solid #eef3f8'
  },
  tableCellHighlighted: {
    backgroundColor: '#e8f5e9'
  },
  note: {
    color: '#6d5a1d',
    backgroundColor: '#fff7de',
    border: '1px solid #f0df9a',
    borderRadius: '10px',
    padding: '10px 12px',
    fontSize: '13px',
    lineHeight: 1.6
  },
  nextAction: {
    color: '#11324d',
    fontSize: '13px',
    fontWeight: 600
  }
};

export default SearchSupportInsightMessage;
